/**
 * variant-picker.js — <variant-picker>
 *
 * Resolves the selected options to a variant, then asks the server to
 * re-render the price, availability and add-to-cart regions.
 *
 * Why the round trip rather than patching from the embedded JSON: price is not
 * a property of a variant alone. Markets, currency, automatic discounts and
 * unit pricing all change it. Rendering those in JavaScript means maintaining a
 * second, worse copy of Shopify's pricing logic — and the moment it disagrees,
 * the storefront has shown a price it will not honour.
 *
 * The embedded JSON is used only to mark combinations unavailable immediately,
 * which is a fact about inventory and safe to know locally.
 */

const PENDING_CLASS = 'is-pending';

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.productUrl = this.dataset.url;
    this.updateUrl = this.dataset.updateUrl !== 'false';
    this.variants = this.readVariants();
    this.abortController = null;

    this.addEventListener('change', this.onChange.bind(this));
    this.markUnavailable();
  }

  readVariants() {
    const script = this.querySelector('[data-variant-data]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return [];
    }
  }

  get selectedOptions() {
    return Array.from(this.querySelectorAll('.variant-option__input:checked'))
      .sort((a, b) => a.dataset.optionPosition - b.dataset.optionPosition)
      .map((input) => input.value);
  }

  findVariant(options) {
    return this.variants.find((variant) =>
      variant.options.every((value, index) => value === options[index])
    );
  }

  /**
   * Marks combinations that cannot be bought, holding every other option
   * fixed. They stay selectable and are struck through rather than hidden:
   * hiding makes the range look smaller than it is, and disabling gives no way
   * to discover what is missing.
   */
  markUnavailable() {
    if (this.variants.length === 0) return;

    const current = this.selectedOptions;

    this.querySelectorAll('.variant-option__input').forEach((input) => {
      const position = parseInt(input.dataset.optionPosition, 10) - 1;
      const candidate = [...current];
      candidate[position] = input.value;

      const match = this.variants.find((variant) =>
        variant.options.every((value, index) => value === candidate[index])
      );

      const unavailable = !match || !match.available;
      input.closest('.variant-option')?.classList.toggle('variant-option--unavailable', unavailable);
    });
  }

  onChange(event) {
    if (!event.target.matches('.variant-option__input')) return;

    this.updateOptionLabel(event.target);
    this.markUnavailable();

    const variant = this.findVariant(this.selectedOptions);
    if (!variant) {
      this.render(null);
      return;
    }

    if (this.updateUrl) {
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    // Media swaps immediately — it is already on the page and waiting for the
    // network to show the right photograph would feel broken.
    if (variant.featured_media) {
      document.dispatchEvent(
        new CustomEvent('theme:variant:media', { detail: { mediaId: variant.featured_media.id } })
      );
    }

    this.render(variant);
  }

  updateOptionLabel(input) {
    const display = input.closest('.variant-group')?.querySelector('[data-option-value-display]');
    if (display) display.textContent = input.value;
  }

  async render(variant) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const container = this.closest('.product-purchase__inner') || document;
    container.classList.add(PENDING_CLASS);

    const params = new URLSearchParams({ section_id: this.sectionId });
    if (variant) params.set('variant', variant.id);

    try {
      const response = await fetch(`${this.productUrl}?${params}`, {
        signal: this.abortController.signal,
      });
      if (!response.ok) throw new Error('Variant update failed');

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');

      ['[data-variant-region-price]', '[data-variant-region-availability]', '[data-variant-region-buy]'].forEach(
        (selector) => {
          const next = parsed.querySelector(selector);
          const current = container.querySelector(selector);
          if (next && current) current.innerHTML = next.innerHTML;
        }
      );

      const hidden = container.querySelector('[data-variant-id]');
      if (hidden && variant) hidden.value = variant.id;

      document.dispatchEvent(new CustomEvent('theme:variant:change', { detail: { variant } }));
      document.dispatchEvent(new CustomEvent('theme:contentchange', { detail: { target: container } }));
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Falling back to a full navigation is slower but always correct.
      if (variant) window.location.search = `?variant=${variant.id}`;
    } finally {
      container.classList.remove(PENDING_CLASS);
      this.abortController = null;
    }
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}

export default VariantPicker;
